"use client"
import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { useQueryClient } from "@tanstack/react-query"

import type { PromptVersionEntry } from "@/components/agents/shared/prompt-history-dialog"
import { AGENT_PROMPT_VERSIONS_QUERY_KEY, type AgentPromptVersionView } from "@/hooks/queries/agent-prompt-versions"
import { useAgentQualityChangesQuery } from "@/hooks/queries/conversation-quality"


import { UndoChangeButton } from "./undo-change-button"
import { useTranslations } from "next-intl"


/**
 * History's label, note and Undo for a version a Conversation Quality change wrote. Each renders its own lookup, so the
 * agent's changes load only once History shows a linked version, and every row shares that one query.
 */
export function qualityChangeSlots(
  version: AgentPromptVersionView,
  context: { agentId: string; canManage: boolean; onUndone: () => void }
): Pick<PromptVersionEntry, "label" | "note" | "action"> {
  const suggestionId = version.quality_suggestion_id
  if (!suggestionId) return {}

  if (version.source === "quality_undo") {
    return {
      label: <TakeBackLabel agentId={context.agentId} suggestionId={suggestionId} />,
      note: <ChangeNote agentId={context.agentId} suggestionId={suggestionId} />,
    }
  }
  return {
    note: <ChangeNote agentId={context.agentId} suggestionId={suggestionId} />,
    action: context.canManage ? (
      <HistoryUndo agentId={context.agentId} suggestionId={suggestionId} onUndone={context.onUndone} />
    ) : undefined,
  }
}

function useChange(agentId: string, suggestionId: string) {
  return useAgentQualityChangesQuery(agentId).data?.find((change) => change.id === suggestionId)
}

function TakeBackLabel({ agentId, suggestionId }: { agentId: string; suggestionId: string }) {
  const { undoneVersionLabel } = useQualityChanges()

  const change = useChange(agentId, suggestionId)
  return <span>{undoneVersionLabel(change?.reason ?? null)}</span>
}

function ChangeNote({ agentId, suggestionId }: { agentId: string; suggestionId: string }) {
  const change = useChange(agentId, suggestionId)
  return change ? <span>{change.title}</span> : null
}

/** Offered while the change is the agent's own; History and the editor reload once it is undone. */
function HistoryUndo({
  agentId,
  suggestionId,
  onUndone,
}: {
  agentId: string
  suggestionId: string
  onUndone: () => void
}) {
  const { canUndoChange } = useQualityChanges()

  const tUi = useTranslations("ConversationQuality.ConversationQualityQualityChangeHistory")

  const change = useChange(agentId, suggestionId)
  const queryClient = useQueryClient()

  if (!change || !canUndoChange(change)) return null

  return (
    <UndoChangeButton
      suggestionId={suggestionId}
      kind="prompt_edit"
      label={tUi("undoThisChange")}
      onUndone={async () => {
        await queryClient.invalidateQueries({ queryKey: AGENT_PROMPT_VERSIONS_QUERY_KEY })
        onUndone()
      }}
    />
  )
}
